'use client';

import { Globe02Icon, Moon02Icon, Sun01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { useLocale } from '@/components/common/locale-provider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslations } from '@/hooks/use-translations';
import { i18n, type Locale, localeNames } from '@/lib/i18n/config';

/**
 * Language and theme controls for auth pages (top-right corner).
 * Standalone versions that don't depend on sidebar context.
 */
export function AuthPageControls() {
  const { locale, setLocale } = useLocale();
  const { setTheme, resolvedTheme } = useTheme();
  const { t } = useTranslations(locale);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="absolute top-4 right-4 z-10 flex items-center gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-foreground"
            aria-label={t('common.changeLanguage') || 'Change language'}
          >
            <HugeiconsIcon
              aria-hidden="true"
              icon={Globe02Icon}
              strokeWidth={2}
              className="size-4"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {i18n.locales.map((loc: Locale) => (
            <DropdownMenuItem
              key={loc}
              className="cursor-pointer"
              onSelect={() => setLocale(loc)}
            >
              <span className={loc === locale ? 'font-medium' : ''}>
                {localeNames[loc]}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="ghost"
        size="icon"
        className="size-8 text-muted-foreground hover:text-foreground"
        onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        aria-label={
          mounted && resolvedTheme === 'dark'
            ? t('common.switchToLightMode') || 'Switch to light mode'
            : t('common.switchToDarkMode') || 'Switch to dark mode'
        }
      >
        {mounted && resolvedTheme === 'dark' ? (
          <HugeiconsIcon
            aria-hidden="true"
            icon={Sun01Icon}
            strokeWidth={2}
            className="size-4"
          />
        ) : (
          <HugeiconsIcon
            aria-hidden="true"
            icon={Moon02Icon}
            strokeWidth={2}
            className="size-4"
          />
        )}
      </Button>
    </div>
  );
}
